import { useCallback, useEffect, useState } from "react";
import { usePrevious } from "../../../hooks/usePrevious";
import {
  FieldTypes,
  IFieldInstance,
} from "../../../types/fieldInstance.interface";
import {
  fieldTypes,
  getInitialValue,
} from "../../../utils/formGenUtils/getInitialValues";
import { getRLType } from "../../../utils/helpers/relatedListHelpers";
import FieldArrayField from "./FieldArrayField";

interface IFieldArrayFieldsProps {
  mainFieldName: string;
  index: number;
  record: any;
  fields: { [x: string]: IFieldInstance[] };
  viewModel: any;
  getError: any;
  selectOptions: any;
  isHidden: (index: number) => string;
}

const FieldArrayFields = ({
  mainFieldName,
  index,
  record,
  fields,
  viewModel,
  getError,
  selectOptions,
  isHidden,
}: IFieldArrayFieldsProps) => {
  const rlType = getRLType(mainFieldName);
  const _fields = Object.values(fields).flat();
  const [recordViewModel, setRecordViewModel] = useState(
    viewModel[rlType as string][index]
  );
  useEffect(() => {
    setRecordViewModel(viewModel[rlType as string][index]);
  }, [viewModel, record]);

  const previousRecord = usePrevious(record);

  function getParentFieldName(field: IFieldInstance) {
    const parentField = _fields.find(
      (_field) => _field.id === field.parentFieldId
    );
    return parentField ? parentField.fieldName : "";
  }

  const shouldCleanupField = useCallback(
    (field: IFieldInstance) => {
      const parentField = getParentFieldName(field);
      const initialValue = getInitialValue(
        field.fieldType as keyof typeof fieldTypes
      );

      if (record[field.fieldName] === initialValue) {
        return false;
      }

      if (field.dataType === "Boolean" && parentField) {
        // is fieldname used multiple times in miniform?
        const fieldDuplicates = _fields.filter(
          (_field) =>
            _field.id !== field.id && _field.fieldName === field.fieldName
        );
        // if fieldname is a duplicate, do not clean up if its duplicate is active somewhere else
        const isDuplicateActive = fieldDuplicates.some(
          (field) => recordViewModel[field.parentFieldId]
        );
        return isDuplicateActive
          ? false
          : !recordViewModel[field.parentFieldId];
      }
      const hasParentValueChanged =
        !!previousRecord?.[parentField] &&
        record[parentField] !== previousRecord?.[parentField];
      return !!parentField && hasParentValueChanged;
    },
    [record, recordViewModel]
  );

  const allWoundFields = _fields.filter(
    (field) => field.fieldType === FieldTypes.CustomWoundComp
  );

  return (
    <div className={isHidden(index) ? "hidden" : ""}>
      {_fields.map((field) => (
        <FieldArrayField
          key={field.id}
          field={field}
          mainFieldName={mainFieldName}
          index={index}
          recordViewModel={recordViewModel}
          getError={getError}
          selectOptions={selectOptions}
          record={record}
          parentField={getParentFieldName(field)}
          shouldCleanupField={shouldCleanupField(field)}
          allWoundFields={allWoundFields}
        />
      ))}
    </div>
  );
};

export default FieldArrayFields;
